import { useEffect, useRef } from 'react';

interface VideoBlockProps {
  src: string;
  objectFit?: 'contain' | 'cover';
  rotation?: number;
  pageId: number;
  boundryId: string | null;
}

const rotationStyle = {
  [180]: {
    transform: 'rotate(180deg)',
  },
  [90]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(90deg) translateY(-100%)',
    WebkitTransform: 'rotate(90deg) translateY(-100%)',
    MozTransform: 'rotate(90deg) translateY(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
  [270]: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transform: 'rotate(270deg) translateX(-100%)',
    WebkitTransform: 'rotate(270deg) translateX(-100%)',
    MozTransform: 'rotate(270deg) translateX(-100%)',
    MozTransformOrigin: 'top left',
    WebkitTransformOrigin: 'top left',
    transformOrigin: 'top left',
    width: '100vh',
    height: '100vw',
  },
} as const;

function VideoBlock({ src, objectFit, rotation = 0, pageId, boundryId = null }: VideoBlockProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current!;
    const pageElement = document.getElementById(boundryId ?? `page_wrapper_${pageId}`);

    const handleOpacityChange = () => {
      const isNotVisible = pageElement?.classList.contains('opacity-0');

      if (isNotVisible) {
        console.log('Video is not visible', boundryId ?? `page_wrapper_${pageId}`);
        if (!video.paused) {
          video.pause();
          video.currentTime = 0;
        }
      } else {
        console.log('Video is visible', boundryId ?? `page_wrapper_${pageId}`);
        if (video.paused) {
          video.play().catch(err => {
            console.warn('Video playback failed (probably due to race):', err);
          });
        }
      }
    };

    // Observe class changes
    const observer = new MutationObserver(() => handleOpacityChange());

    if (pageElement) {
      observer.observe(pageElement, { attributes: true, attributeFilter: ['class'] });
      handleOpacityChange(); // Initial check
    }

    return () => observer.disconnect();
  }, [boundryId, pageId]);


  return (
    <div className="w-full h-full relative bg-inherit">
      <video
        ref={videoRef}
        controls={false}
        autoPlay
        loop
        preload="auto"
        muted
        className="w-full h-full bg-inherit"
        style={{
          objectFit: objectFit ?? 'contain',
          ...(rotation !== 0 ? rotationStyle[rotation.toString() as unknown as keyof typeof rotationStyle] : {}),
        }}
      >
        <source src={src} type="video/mp4" />
        <source src={src} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export default VideoBlock;
